import React, { useState, createContext, useContext, useEffect, useCallback } from "react";
import { useApiService } from "../shared/apiservice";
import { useAuthService } from "../shared/authservice";
import { AuthResponse, AuthUser } from "../shared/types";

const authContext = createContext<{
    user: AuthUser | null;
    errors: string[];
    loading: boolean;
    login: (email: string, password: string, rememberMe: boolean) => void;
    logout: () => void;
    refreshUser: () => Promise<AuthUser | null>;
    isloggedIn: boolean;
}>({
    user: null,
    errors: [],
    loading: false,
    login: () => { },
    logout: () => { },
    refreshUser: async () => null,
    isloggedIn: false,
});

const AUTH_USER_STORAGE_KEY = "auth_user";

function useProviderAuth() {
    const [user, setUser] = useState<AuthUser | null>(() => {
        try {
            const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY) ?? window.sessionStorage.getItem(AUTH_USER_STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as AuthUser;
        } catch {
            return null;
        }
    });
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();
    const authService = useAuthService();

    const [isloggedIn, setIsLoggedIn] = useState(() =>
        authService.isAuthenticated()
    );

    const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
        setLoading(true);
        setErrors([]);
        try {
            const response = await apiService.get("/user");
            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                }
                return null;
            }

            const json = await response.json();
            const rawUser =
                json && typeof json === "object" && "data" in (json as any)
                    ? (json as any).data
                    : json;

            const parsed = (rawUser ?? null) as AuthUser | null;
            setUser(parsed);
            if (parsed) {
                setIsLoggedIn(true);
                const serialized = JSON.stringify(parsed);
                // Store alongside token persistence.
                if (localStorage.getItem("token")) {
                    localStorage.setItem(AUTH_USER_STORAGE_KEY, serialized);
                    window.sessionStorage.removeItem(AUTH_USER_STORAGE_KEY);
                } else {
                    window.sessionStorage.setItem(AUTH_USER_STORAGE_KEY, serialized);
                    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
                }
            }
            return parsed;
        } catch (error) {
            console.error(error);
            setErrors(["Loading user details failed. Please try again."]);
            return null;
        } finally {
            setLoading(false);
        }
    }, [apiService]);

    useEffect(() => {
        const authed = authService.isAuthenticated();
        if (authed !== isloggedIn) {
            setIsLoggedIn(authed);
        }
        if (authed) {
            // Keep user hydrated even if a route loads before login flow completes.
            if (user === null && !loading) {
                refreshUser().catch(() => { });
            }
        }
        // Intentionally do not clear `user` here; only `logout()` clears it.
    }, [authService, isloggedIn, user, loading, refreshUser]);

    async function login(email: string, password: string, rememberMe: boolean) {
        setLoading(true);
        setErrors([]);
        let device_name = navigator.userAgent;
        try {
            let response = await apiService.post('/auth/login', { email, password, device_name });
            if (!response.ok) {
                setErrors([
                    'Login failed. Please check your email and password.',
                ]);
                return;
            }
            let data: AuthResponse = await response.json();

            let token = data.token;
            authService.setAuthToken(token, rememberMe);
            await refreshUser();
            setIsLoggedIn(true);
        } catch (error) {
            console.error(error);
            setErrors([
                'Login failed. Please check your email and password.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    function logout() {
        setLoading(true);
        setErrors([]);
        authService.clearAuthToken();
        setUser(null);
        localStorage.removeItem(AUTH_USER_STORAGE_KEY);
        window.sessionStorage.removeItem(AUTH_USER_STORAGE_KEY);
        setIsLoggedIn(false);
        setLoading(false);
    }

    return {
        user,
        errors,
        loading,
        login,
        logout,
        refreshUser,
        isloggedIn,
    }
}

export function ProviderAuth({ children }: { children: React.ReactNode }) {
    const auth = useProviderAuth();
    return <authContext.Provider value={auth}> {children}</authContext.Provider>;
}

export const useAuth = () => {
    return useContext(authContext);
}